import {
  Button,
  Center,
  Loader,
  Flex,
  Text,
  Badge,
  Tooltip,
  Modal,
} from '@mantine/core';
import { useState } from 'react';
import { useBusiness, useNotification, useUser } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { GenericErrorFallback } from '../../../shared/components/GenericErrorFallback';
import DevToolsCard from './DevToolsCard';
import { useNavigate, useParams } from 'react-router-dom';
import { PageNotFoundPage } from '../../../shared/components/PageNotFoundPage';
import NewTable from '../../../shared/components/NewTable/NewTable';
import { CellValue } from '../../../shared/components/NewTable/useColumns';
import { WebhookAttemptResponseDto } from '../../../shared/types';
import {
  useDeleteWebhook,
  useListWebhooks,
  useSendTestMessage,
  useWebhookSecret,
} from '../../../hooks/webhooksApi';
import { useMessageAttempts } from '../../../hooks/webhooksApi/useMessageAttempts';
import classes from './DevTools.module.css';
import { CopyButton, Grid } from '@mantine/core';
import { useToggle } from '@mantine/hooks';
import { BetaIcon } from '../../../../lib/components';

export type WebhookAddFormData = {
  url: string;
  description: string;
  events: string[];
};

const WEBHOOK_LIST_ROUTE = '/admin/dev-tools/webhooks';
const MASKED_SECRET_KEY = '..........................';

const STATUS_TO_COLOR: Record<WebhookAttemptResponseDto['status'], string> = {
  SUCCESS: 'var(--mantine-color-green-2)',
  PENDING: '#7a7a7a',
  FAILED: 'var(--mantine-color-red-2)',
  SENDING: 'blue',
};

const MESSAGE_ATTEMPTS_POLL_INTERVAL_IN_MS = 6000;

export const WebhooksDetails = () => {
  const { t } = useTranslation();
  const { data: user, isError } = useUser();
  const navigate = useNavigate();
  const { id } = useParams();
  const [responseToPreview, setResponseToPreview] = useState<string | null>(
    null
  );
  const [visibleSecret, setVisibleSecret] = useToggle();
  const { data: business, isLoading } = useBusiness(user?.businessId);

  const listWebhooks = useListWebhooks();
  const listMessageAttempts = useMessageAttempts({
    webhookId: id,
    pollInterval: MESSAGE_ATTEMPTS_POLL_INTERVAL_IN_MS,
  });
  const deleteWebhook = useDeleteWebhook();
  const sendTestMessage = useSendTestMessage({
    webhookId: id,
  });
  const { showSuccess } = useNotification();
  const { mutateAsync: getWebhookSecret, data: webhookSecretData } =
    useWebhookSecret();

  if (isLoading) {
    return (
      <Center w="100%" h="100vh">
        <Loader />
      </Center>
    );
  }

  if (isError || !business || !user) {
    return <GenericErrorFallback />;
  }

  if (listWebhooks.isLoading) {
    return <Loader />;
  }

  const webhook = listWebhooks.data?.find((webhook) => webhook.id === id);

  if (!webhook || !id) {
    return <PageNotFoundPage />;
  }

  const handleDeleteWebhook = async () => {
    const response = await deleteWebhook.mutateAsync(id);
    await listWebhooks.refetch();

    if (response.ok) {
      navigate(WEBHOOK_LIST_ROUTE);
    }
  };

  const handleSendTestMessage = async () => {
    if (!webhook?.events[0]) {
      return;
    }
    await sendTestMessage.mutateAsync({
      id,
      eventType: webhook?.events[0],
    });

    showSuccess(t('devTools.webhooksDetails.testMessageSent'));
  };

  const handleSecretClick = () => {
    if (!webhookSecretData?.secret) {
      getWebhookSecret(id);
    }
    setVisibleSecret((prev) => !prev);
  };

  const sortedAttempts = listMessageAttempts.data?.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const canDisplaySecret = webhookSecretData?.secret && visibleSecret;

  return (
    <DevToolsCard title={webhook?.url}>
      {webhook?.description && (
        <Text>
          {t('devTools.webhooks.description')}: {webhook?.description}
        </Text>
      )}

      <Flex mt="lg" gap={{ base: 'xs', sm: 'lg' }}>
        <Flex direction="column" gap="sm" className={classes.detailsRow}>
          <Text fw={500}>{t('devTools.webhooks.status')}</Text>
          <Badge color={webhook?.isActive ? 'green' : 'red'}>
            {webhook?.isActive
              ? t('devTools.webhooks.active')
              : t('devTools.webhooks.inactive')}
          </Badge>
        </Flex>
        <Flex direction="column" gap="sm" className={classes.detailsRow}>
          <Text fw={500}>{t('devTools.webhooksDetails.listeningFor')}</Text>
          <Tooltip
            w={180}
            label={webhook?.events.join(' ')}
            multiline
            transitionProps={{ transition: 'fade', duration: 300 }}
          >
            <Badge color="#7a7a7a">
              {webhook?.events.length} {t('devTools.webhooks.events')}
            </Badge>
          </Tooltip>
        </Flex>
        <Flex direction="column" gap="xs">
          <Text fw={500} style={{ wordBreak: 'break-all' }}>
            {t('devTools.webhooksDetails.signingSecret')}
          </Text>

          <div style={{ height: '2rem' }}>
            <Flex>
              <Button
                variant="transparent"
                size="xs"
                onClick={handleSecretClick}
                pl={1}
                pr={0}
                mr={'xs'}
                title={t<string>('devTools.webhooksDetails.showSecret')}
                styles={{
                  root: {
                    padding: 0,
                  },
                  inner: {
                    padding: 0,
                    display: 'block',
                  },
                }}
              >
                <BetaIcon icon={canDisplaySecret ? 'eye-slash' : 'eye'} />
              </Button>
              {canDisplaySecret ? (
                <CopyButton value={webhookSecretData.secret}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={
                        copied
                          ? t('devTools.webhooksDetails.copied')
                          : t('devTools.webhooksDetails.clickToCopy')
                      }
                      withArrow
                      position="top"
                    >
                      <Flex>
                        <Text onClick={copy} pt="4" mr="6">
                          {webhookSecretData.secret}
                        </Text>
                        <BetaIcon
                          icon={!copied ? 'copy' : 'check'}
                          onClick={copy}
                          size="md"
                          style={{ marginTop: '4px', fontSize: '18px' }}
                        />
                      </Flex>
                    </Tooltip>
                  )}
                </CopyButton>
              ) : (
                <Text size="xl" lh="1" pt="1">
                  {MASKED_SECRET_KEY}
                </Text>
              )}
            </Flex>
          </div>
        </Flex>
      </Flex>

      <NewTable
        header={{
          title: t<string>('devTools.webhooksDetails.lastMessageAttempts'),
        }}
        data={sortedAttempts}
        columns={[
          {
            value: 'status',
            label: t<string>('devTools.webhooks.status'),
            renderText: (status: CellValue) => {
              return (
                <Badge
                  color={
                    STATUS_TO_COLOR[
                      status as WebhookAttemptResponseDto['status']
                    ]
                  }
                >
                  {status}
                </Badge>
              );
            },
          },
          {
            value: 'msg.eventType',
            label: t<string>('devTools.webhooksDetails.eventType'),
            renderText: (eventType: CellValue) => {
              return <code>{eventType}</code>;
            },
          },
          {
            value: 'timestamp',
            label: t<string>('devTools.webhooksDetails.timestamp'),
            renderText: (timestamp: CellValue) => {
              if (!timestamp) return <Text>N/A</Text>;

              return <Text>{new Date(timestamp).toLocaleString()}</Text>;
            },
          },
          {
            value: 'response',
            label: t<string>('devTools.webhooksDetails.response'),
            renderText: (response: CellValue) => {
              return (
                <Text
                  onClick={() =>
                    typeof response === 'string'
                      ? setResponseToPreview(response)
                      : undefined
                  }
                  style={{ cursor: 'pointer' }}
                >
                  {t('devTools.webhooksDetails.seeResponse')}
                </Text>
              );
            },
          },
        ]}
        loading={listMessageAttempts.isLoading}
        empty={{
          title: t<string>('devTools.webhooksDetails.noMessageAttemptsYet'),
          buttonLabel: t<string>('devTools.webhooksDetails.sendTestMessage'),
          action: undefined,
          onClick: handleSendTestMessage,
          icon: 'paper-plane',
        }}
        height={(sortedAttempts?.length ?? 0) > 0 ? '400px' : 'auto'}
        style={{
          marginTop: '2rem',
          top: 0,
          width: '100%',
          position: 'relative',
          left: 0,
        }}
      />

      <Grid>
        <Grid.Col
          span={{
            base: 12,
            xs: 4,
          }}
          order={{
            base: 2,
            xs: 1,
          }}
        >
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteWebhook}
            loading={deleteWebhook.isLoading || listWebhooks.isLoading}
            className={classes.deleteButton}
          >
            {t('common.delete')}
          </Button>
        </Grid.Col>
        <Grid.Col
          span={{
            base: 12,
            xs: 8,
          }}
          order={{
            base: 1,
            xs: 2,
          }}
        >
          <Flex
            justify="end"
            gap="sm"
            direction={{ base: 'column', xs: 'row' }}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(WEBHOOK_LIST_ROUTE)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendTestMessage}
              loading={sendTestMessage.isLoading}
            >
              {t('devTools.webhooksDetails.sendTestMessage')}
            </Button>
          </Flex>
        </Grid.Col>
      </Grid>
      <Modal
        opened={!!responseToPreview}
        onClose={() => setResponseToPreview(null)}
        title={t('devTools.webhooksDetails.response')}
        centered
        styles={{
          body: {
            wordBreak: 'break-all',
          },
        }}
      >
        <Text size="xs">
          <code>{responseToPreview}</code>
        </Text>
      </Modal>
    </DevToolsCard>
  );
};
